import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { collection, getDocs, doc, updateDoc, getDoc, addDoc, query, where } from 'firebase/firestore';
import { LuClipboardList, LuFilterX, LuHospital, LuHouse, LuLoaderCircle, LuPlus, LuSave, LuTableProperties } from 'react-icons/lu';
import { toast } from 'react-toastify';
import { db } from '../../config/firebaseConfig';
import Button from '../../components/ui/Button';
import GlassIcon from '../../components/ui/GlassIcon';
import Modal from '../../components/ui/Modal';
import SegmentedControl from '../../components/ui/SegmentedControl';
import { INPUT_CLASS, SelectInput } from '../../components/form/FormControls';
import HastaneDataEntryContent from './HastaneDataEntryContent';
import EntryGrid from './parts/EntryGrid';
import { homeEntryColumns } from './parts/entryColumns';
import { EntryCount, EntryEmpty, EntryFooterInfo } from './parts/entryBits';
import useSmoText from './smoText';

// Native select (a project can appear twice, so SelectInput's value keys don't fit)
const SELECT = `${INPUT_CLASS} tw-cursor-pointer`;

// Blank row template matching the Excel columns
const createBlankRow = () => ({
  _isNew: true,
  _visitId: null,
  projectId: '',
  patientId: '',
  visitNumber: '',
  windowOpenDate: '',
  targetDate: '',
  windowCloseDate: '',
  clinicianFullName: '',
  scheduledDate: '',
  scheduledTime: '',
  travelBeforeHours: '',
  travelBeforeMinutes: '',
  travelBeforeDistance: '',
  visitStartDate: '',
  visitStartTime: '',
  visitEndDate: '',
  visitEndTime: '',
  visitDurationHours: '',
  visitDurationMinutes: '',
  visitDistance: '',
  travelAfterHours: '',
  travelAfterMinutes: '',
  travelAfterDistance: '',
  issueOccurred: '',
  issueResultedInMiss: '',
  issueSource: '',
  smoCorrectionRequests: '',
  clinicalPMCorrections: '',
  correctionReason: '',
  dateInitialSourceReceived: '',
  dateLatestSourceRevision: 'N/A',
  dateSourceFinalized: '',
  wasAeSaeTaken: '',
  wasDcfCreated: '',
});

// Map a Firestore visit doc → table row
const visitToRow = (v) => {
  const ev = v.visitEvaluation || {};
  return {
    _isNew: false,
    _visitId: v.id,
    projectId: v.projectId || '',
    patientId: v.patientId || '',
    visitNumber: v.visitNumber || '',
    windowOpenDate: ev.windowOpenDate || '',
    targetDate: ev.targetDate || '',
    windowCloseDate: ev.windowCloseDate || '',
    clinicianFullName: v.clinicianFullName || ev.clinicianFullName || '',
    scheduledDate: v.firstVisitDate || v.date || '',
    scheduledTime: v.startTime || '',
    travelBeforeHours: ev.travelTimeBefore ? String(Math.floor(Number(ev.travelTimeBefore) / 60)) : '',
    travelBeforeMinutes: ev.travelTimeBefore ? String(Number(ev.travelTimeBefore) % 60) : '',
    travelBeforeDistance: ev.travelDistanceBefore || '',
    visitStartDate: v.visitStartDate || v.date || '',
    visitStartTime: v.visitStartTime || v.startTime || '',
    visitEndDate: v.visitEndDate || ev.visitEndDate || '',
    visitEndTime: v.visitEndTime || v.endTime || '',
    visitDurationHours: ev.visitDurationHours || '',
    visitDurationMinutes: ev.visitDurationMinutes || '',
    visitDistance: ev.visitDistance || '',
    travelAfterHours: ev.travelTimeAfter ? String(Math.floor(Number(ev.travelTimeAfter) / 60)) : '',
    travelAfterMinutes: ev.travelTimeAfter ? String(Number(ev.travelTimeAfter) % 60) : '',
    travelAfterDistance: ev.travelDistanceAfter || '',
    issueOccurred: ev.issueOccurred || '',
    issueResultedInMiss: ev.issueResultedInMiss || '',
    issueSource: ev.issueCategory || '',
    smoCorrectionRequests: ev.smoCorrectionRequests || '',
    clinicalPMCorrections: ev.qualityManagerCheck || '',
    correctionReason: ev.correctionReason || '',
    dateInitialSourceReceived: ev.dateInitialSourceReceived || '',
    dateLatestSourceRevision: ev.dateLatestSourceRevision || 'N/A',
    dateSourceFinalized: ev.dateSourceFinalized || '',
    wasAeSaeTaken: ev.wasAeSaeTaken || '',
    wasDcfCreated: ev.dcfCreated || '',
  };
};

// Map a table row → Firestore fields for save
const rowToFirestore = (row, employeeId, employeeName, projectId) => {
  const travelBefore = (Number(row.travelBeforeHours) || 0) * 60 + (Number(row.travelBeforeMinutes) || 0);
  const travelAfter = (Number(row.travelAfterHours) || 0) * 60 + (Number(row.travelAfterMinutes) || 0);

  return {
    patientId: row.patientId,
    visitNumber: row.visitNumber,
    date: row.scheduledDate,
    startTime: row.scheduledTime,
    visitStartDate: row.visitStartDate,
    visitStartTime: row.visitStartTime,
    visitEndDate: row.visitEndDate,
    visitEndTime: row.visitEndTime,
    endTime: row.visitEndTime,
    clinicianFullName: row.clinicianFullName,
    employeeId,
    employeeName,
    projectId: projectId || row.projectId || '',
    visitType: 'home',
    lastUpdated: new Date().toISOString(),
    visitEvaluation: {
      windowOpenDate: row.windowOpenDate,
      targetDate: row.targetDate,
      windowCloseDate: row.windowCloseDate,
      clinicianFullName: row.clinicianFullName,
      travelTimeBefore: travelBefore > 0 ? String(travelBefore) : '',
      travelDistanceBefore: row.travelBeforeDistance,
      visitDurationHours: row.visitDurationHours,
      visitDurationMinutes: row.visitDurationMinutes,
      visitDistance: row.visitDistance,
      travelTimeAfter: travelAfter > 0 ? String(travelAfter) : '',
      travelDistanceAfter: row.travelAfterDistance,
      issueOccurred: row.issueOccurred,
      issueResultedInMiss: row.issueResultedInMiss,
      issueCategory: row.issueSource,
      smoCorrectionRequests: row.smoCorrectionRequests,
      qualityManagerCheck: row.clinicalPMCorrections,
      correctionReason: row.correctionReason,
      dateInitialSourceReceived: row.dateInitialSourceReceived,
      dateLatestSourceRevision: row.dateLatestSourceRevision || 'N/A',
      dateSourceFinalized: row.dateSourceFinalized,
      wasAeSaeTaken: row.wasAeSaeTaken,
      dcfCreated: row.wasDcfCreated,
    },
  };
};

const EMPTY_FILTERS = {
  projectId: '',
  patientId: '',
  visitNumber: '',
  windowOpenDateFrom: '', windowOpenDateTo: '',
  targetDateFrom: '', targetDateTo: '',
  windowCloseDateFrom: '', windowCloseDateTo: '',
  clinicianFullName: '',
  scheduledDateFrom: '', scheduledDateTo: '',
  visitStartDateFrom: '', visitStartDateTo: '',
  visitEndDateFrom: '', visitEndDateTo: '',
  issueOccurred: '',
  issueResultedInMiss: '',
  issueSource: '',
  smoCorrectionRequests: '',
  clinicalPMCorrections: '',
  correctionReason: '',
  dateInitialSourceReceivedFrom: '', dateInitialSourceReceivedTo: '',
  dateLatestSourceRevisionFrom: '', dateLatestSourceRevisionTo: '',
  dateSourceFinalizedFrom: '', dateSourceFinalizedTo: '',
  wasAeSaeTaken: '',
  wasDcfCreated: '',
};

const ManualDataEntryModal = ({ show, onHide, workers, selectedSMO, onDataSaved }) => {
  const { t, label, language } = useSmoText();
  // Toasts in callbacks read the current language through a ref
  const labelRef = useRef(label);
  labelRef.current = label;
  const [activeTab, setActiveTab] = useState('home');
  const [selectedWorkerId, setSelectedWorkerId] = useState('');
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [selectedPatientId, setSelectedPatientId] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [showFilters, setShowFilters] = useState(false);
  const [projectsMap, setProjectsMap] = useState({});

  // Load all projects once to map projectId → display title
  useEffect(() => {
    if (!show) return;
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDocs(collection(db, 'Projects'));
        if (cancelled) return;
        const map = {};
        snap.docs.forEach(d => {
          const data = d.data() || {};
          map[d.id] = data.titleShort || data.titleLong || data.title || '';
        });
        setProjectsMap(map);
      } catch (err) {
        console.error('Error loading projects:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [show]);

  const selectedWorker = workers.find(w => w.id === selectedWorkerId);
  const workerProjects = selectedWorker?.projects || [];

  // Extract unique patient IDs from rows for dropdown
  const uniquePatientIds = useMemo(() => {
    const patientIds = rows.map(row => row.patientId).filter(Boolean);
    return [...new Set(patientIds)].sort();
  }, [rows]);

  // Unique projects present in current rows (for filter dropdown)
  const availableRowProjects = useMemo(() => {
    const ids = [...new Set(rows.map(r => r.projectId).filter(Boolean))];
    return ids
      .map(id => ({ id, title: projectsMap[id] || id }))
      .sort((a, b) => a.title.localeCompare(b.title, 'tr'));
  }, [rows, projectsMap]);

  const setFilter = useCallback((key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  }, []);

  const hasActiveFilters = Object.values(filters).some(v => v !== '') || selectedPatientId !== '';

  const clearFilters = () => {
    setFilters(EMPTY_FILTERS);
    setSelectedPatientId('');
  };

  const toggleFilters = useCallback(() => {
    setShowFilters(prev => !prev);
  }, []);

  // Load visits when employee is selected
  const loadVisits = useCallback(async () => {
    if (!selectedWorkerId) { setRows([]); return; }
    setLoading(true);
    try {
      const visitsRef = collection(db, 'Visits');
      // For home visits, only filter by employee and visit type (no project filter)
      const q = query(visitsRef, where('employeeId', '==', selectedWorkerId), where('visitType', '==', 'home'));
      const snap = await getDocs(q);
      const visitRows = snap.docs.map(d => visitToRow({ id: d.id, ...d.data() }));
      // Sort: oldest first (newest at bottom)
      visitRows.sort((a, b) => {
        const dateA = a.scheduledDate || '';
        const dateB = b.scheduledDate || '';
        if (dateA !== dateB) return dateA.localeCompare(dateB);
        const timeA = a.scheduledTime || '';
        const timeB = b.scheduledTime || '';
        return timeA.localeCompare(timeB);
      });
      setRows(visitRows);
    } catch (err) {
      console.error('Error loading visits:', err);
      toast.error(labelRef.current('entry.toast.loadError'));
    } finally {
      setLoading(false);
    }
  }, [selectedWorkerId]);

  useEffect(() => {
    if (show && selectedWorkerId) {
      loadVisits();
    } else if (!selectedWorkerId) {
      setRows([]);
    }
  }, [show, selectedWorkerId, loadVisits]);

  // Reset on close
  useEffect(() => {
    if (!show) {
      setSelectedWorkerId('');
      setSelectedProjectId('');
      setSelectedPatientId('');
      setRows([]);
      setFilters(EMPTY_FILTERS);
    }
  }, [show]);

  // Reset filters when worker changes
  useEffect(() => {
    setFilters(EMPTY_FILTERS);
    setSelectedPatientId('');
  }, [selectedWorkerId]);

  // Filtered rows (display only — rows state is source of truth for saving)
  const filteredRows = useMemo(() => {
    const dateInRange = (val, from, to) => {
      if (!from && !to) return true;
      if (!val) return false;
      if (from && val < from) return false;
      if (to && val > to) return false;
      return true;
    };

    return rows
      .map((row, originalIndex) => ({ row, originalIndex }))
      .filter(({ row }) => {
        // Patient ID filter (from dropdown)
        if (selectedPatientId && row.patientId !== selectedPatientId) return false;

        // Project filter
        if (filters.projectId && row.projectId !== filters.projectId) return false;
        
        // Text filters
        if (filters.patientId && !row.patientId.toLowerCase().includes(filters.patientId.toLowerCase())) return false;
        if (filters.visitNumber && !String(row.visitNumber).toLowerCase().includes(filters.visitNumber.toLowerCase())) return false;
        if (filters.clinicianFullName && !row.clinicianFullName.toLowerCase().includes(filters.clinicianFullName.toLowerCase())) return false;
        if (filters.issueSource && !row.issueSource.toLowerCase().includes(filters.issueSource.toLowerCase())) return false;
        if (filters.smoCorrectionRequests && !row.smoCorrectionRequests.toLowerCase().includes(filters.smoCorrectionRequests.toLowerCase())) return false;
        if (filters.clinicalPMCorrections && !row.clinicalPMCorrections.toLowerCase().includes(filters.clinicalPMCorrections.toLowerCase())) return false;
        if (filters.correctionReason && !row.correctionReason.toLowerCase().includes(filters.correctionReason.toLowerCase())) return false;
        // Select filters
        if (filters.issueOccurred && row.issueOccurred !== filters.issueOccurred) return false;
        if (filters.issueResultedInMiss && !row.issueResultedInMiss.toLowerCase().includes(filters.issueResultedInMiss.toLowerCase())) return false;
        if (filters.wasAeSaeTaken && row.wasAeSaeTaken !== filters.wasAeSaeTaken) return false;
        if (filters.wasDcfCreated && row.wasDcfCreated !== filters.wasDcfCreated) return false;
        // Date range filters
        if (!dateInRange(row.windowOpenDate, filters.windowOpenDateFrom, filters.windowOpenDateTo)) return false;
        if (!dateInRange(row.targetDate, filters.targetDateFrom, filters.targetDateTo)) return false;
        if (!dateInRange(row.windowCloseDate, filters.windowCloseDateFrom, filters.windowCloseDateTo)) return false;
        if (!dateInRange(row.scheduledDate, filters.scheduledDateFrom, filters.scheduledDateTo)) return false;
        if (!dateInRange(row.visitStartDate, filters.visitStartDateFrom, filters.visitStartDateTo)) return false;
        if (!dateInRange(row.visitEndDate, filters.visitEndDateFrom, filters.visitEndDateTo)) return false;
        if (!dateInRange(row.dateInitialSourceReceived, filters.dateInitialSourceReceivedFrom, filters.dateInitialSourceReceivedTo)) return false;
        if (!dateInRange(row.dateLatestSourceRevision, filters.dateLatestSourceRevisionFrom, filters.dateLatestSourceRevisionTo)) return false;
        if (!dateInRange(row.dateSourceFinalized, filters.dateSourceFinalizedFrom, filters.dateSourceFinalizedTo)) return false;
        return true;
      });
  }, [rows, filters, selectedPatientId]);

  const handleCellChange = (index, field, value) => {
    setRows(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const addRow = () => {
    setRows(prev => [...prev, createBlankRow()]);
  };

  const removeRow = (index) => {
    setRows(prev => prev.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    if (rows.length === 0) { toast.warn(labelRef.current('entry.toast.nothingToSave')); return; }
    if (!selectedWorkerId) { toast.warn(labelRef.current('entry.toast.pickWorker')); return; }

    const worker = workers.find(w => w.id === selectedWorkerId);
    const employeeName = worker ? `${worker.name} ${worker.surname}` : '';

    setSaving(true);
    let savedCount = 0;
    let errorCount = 0;

    try {
      for (const row of rows) {
        const data = rowToFirestore(row, selectedWorkerId, employeeName, selectedProjectId);
        try {
          if (row._visitId && !row._isNew) {
            // Update existing
            const visitRef = doc(db, 'Visits', row._visitId);
            const currentDoc = await getDoc(visitRef);
            if (currentDoc.exists()) {
              const currentData = currentDoc.data();
              const merged = {
                ...currentData,
                ...data,
                visitEvaluation: {
                  ...currentData.visitEvaluation,
                  ...data.visitEvaluation,
                },
              };
              await updateDoc(visitRef, merged);
            } else {
              await updateDoc(visitRef, data);
            }
            savedCount++;
          } else {
            // Create new
            await addDoc(collection(db, 'Visits'), {
              ...data,
              createdAt: new Date().toISOString(),
              manualEntry: true,
            });
            savedCount++;
          }
        } catch (err) {
          console.error('Error saving row:', err);
          errorCount++;
        }
      }

      if (savedCount > 0) {
        toast.success(labelRef.current('entry.toast.saved', { count: savedCount }));
        if (onDataSaved) onDataSaved();
        await loadVisits(); // reload fresh data
      }
      if (errorCount > 0) {
        toast.error(labelRef.current('entry.toast.saveFailed', { count: errorCount }));
      }
    } catch (err) {
      console.error('Save error:', err);
      toast.error(labelRef.current('entry.toast.saveError'));
    } finally {
      setSaving(false);
    }
  };

  if (!show) return null;

  const existingCount = rows.filter(r => !r._isNew).length;
  const newCount = rows.filter(r => r._isNew).length;
  const { groups, columns } = homeEntryColumns({ label, projectsMap, availableRowProjects, change: handleCellChange });

  return (
    <Modal
      isOpen={!!show}
      onClose={onHide}
      size="full"
      title={label('entry.title')}
      subtitle={`${selectedSMO ? `${selectedSMO.name} ${selectedSMO.surname}` : 'SMO'} — ${label('entry.subtitle')}`}
      closeLabel={t('common.close')}
      lang={language}
      bodyClassName="tw-flex tw-flex-col tw-p-0"
      icon={(
        <GlassIcon gradient="tw-from-sky-400 tw-to-blue-600 tw-shadow-sky-500/30" size="xs">
          <LuTableProperties aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
        </GlassIcon>
      )}
      footer={(
        <>
          <div className="tw-mr-auto tw-min-w-0">
            {activeTab === 'home' && rows.length > 0 && (
              <EntryFooterInfo shown={filteredRows.length} total={rows.length} filtered={hasActiveFilters} existing={existingCount} added={newCount} />
            )}
          </div>
          <Button variant="ghost" onClick={onHide}>{label('entry.cancel')}</Button>
          {activeTab === 'home' && (
            <Button variant="primary" onClick={handleSave} disabled={saving || rows.length === 0}>
              {saving ? <LuLoaderCircle aria-hidden="true" className="tw-h-4 tw-w-4 tw-animate-spin" /> : <LuSave aria-hidden="true" className="tw-h-4 tw-w-4" />}
              {saving ? label('entry.saving') : label('entry.saveAll')}
            </Button>
          )}
        </>
      )}
    >
      {/* Tabs + selectors */}
      <div className="tw-shrink-0 tw-space-y-3 tw-border-b tw-border-slate-200/80 tw-px-4 tw-py-3 sm:tw-px-6 dark:tw-border-white/10">
        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3">
          <SegmentedControl
            layoutId="smo-manual-entry-tab"
            label={label('entry.tabs.label')}
            value={activeTab}
            onChange={setActiveTab}
            options={[
              { value: 'home', label: <span className="tw-inline-flex tw-items-center tw-gap-1.5"><LuHouse aria-hidden="true" className="tw-h-4 tw-w-4" />{label('entry.tabs.home')}</span> },
              { value: 'hastane', label: <span className="tw-inline-flex tw-items-center tw-gap-1.5"><LuHospital aria-hidden="true" className="tw-h-4 tw-w-4" />{label('entry.tabs.site')}</span> },
            ]}
          />
          <p className="tw-min-w-0 tw-flex-1 tw-text-xs tw-leading-relaxed tw-text-slate-500 dark:tw-text-slate-400">{label('entry.help')}</p>
        </div>

        <div className="tw-flex tw-flex-wrap tw-items-end tw-gap-3">
          <label className="tw-block tw-w-full sm:tw-w-56">
            <span className="tw-mb-1 tw-block tw-text-xs tw-font-semibold tw-text-slate-600 dark:tw-text-slate-300">{label('entry.worker')}</span>
            <SelectInput
              value={selectedWorkerId}
              onChange={e => setSelectedWorkerId(e.target.value)}
              placeholder={label('entry.chooseWorker')}
              options={workers.map(w => ({ value: w.id, label: `${w.name} ${w.surname}` }))}
            />
          </label>

          {activeTab === 'hastane' && selectedWorker && workerProjects.length > 0 && (
            <label className="tw-block tw-w-full sm:tw-w-56">
              <span className="tw-mb-1 tw-block tw-text-xs tw-font-semibold tw-text-slate-600 dark:tw-text-slate-300">{label('entry.project')}</span>
              <select
                value={selectedProjectId}
                onChange={e => setSelectedProjectId(e.target.value)}
                className={SELECT}
              >
                <option value="">{label('entry.allProjects')}</option>
                {workerProjects.map((p, i) => (
                  // Aynı proje birden fazla atamayla gelebilir
                  // eslint-disable-next-line react/no-array-index-key
                  <option key={`${p.projectId}-${i}`} value={p.projectId}>{p.projectTitle}</option>
                ))}
              </select>
            </label>
          )}

          {activeTab === 'home' && selectedWorkerId && availableRowProjects.length > 0 && (
            <label className="tw-block tw-w-full sm:tw-w-56">
              <span className="tw-mb-1 tw-block tw-text-xs tw-font-semibold tw-text-slate-600 dark:tw-text-slate-300">{label('entry.project')}</span>
              <SelectInput
                value={filters.projectId}
                onChange={e => setFilter('projectId', e.target.value)}
                placeholder={label('entry.allProjects')}
                options={availableRowProjects.map(p => ({ value: p.id, label: p.title }))}
              />
            </label>
          )}

          {activeTab === 'home' && selectedWorkerId && uniquePatientIds.length > 0 && (
            <label className="tw-block tw-w-full sm:tw-w-44">
              <span className="tw-mb-1 tw-block tw-text-xs tw-font-semibold tw-text-slate-600 dark:tw-text-slate-300">{label('entry.patient')}</span>
              <SelectInput
                value={selectedPatientId}
                onChange={e => setSelectedPatientId(e.target.value)}
                placeholder={label('entry.allPatients')}
                options={uniquePatientIds.map(patientId => ({ value: patientId, label: patientId }))}
              />
            </label>
          )}

          {activeTab === 'home' && rows.length > 0 && (
            <EntryCount shown={filteredRows.length} total={rows.length} filtered={hasActiveFilters} existing={existingCount} added={newCount} />
          )}

          {activeTab === 'home' && hasActiveFilters && (
            <Button size="sm" variant="ghost" onClick={clearFilters} title={label('entry.clearFiltersHint')}>
              <LuFilterX aria-hidden="true" className="tw-h-4 tw-w-4" />
              {label('entry.clearFilters')}
            </Button>
          )}

          {activeTab === 'home' && selectedWorkerId && (
            <Button size="sm" variant="success" onClick={addRow}>
              <LuPlus aria-hidden="true" className="tw-h-4 tw-w-4" />
              {label('entry.addRow')}
            </Button>
          )}
        </div>
      </div>

      {/* Hastane Tab Content */}
      {activeTab === 'hastane' && (
        <HastaneDataEntryContent
          workers={workers}
          selectedSMO={selectedSMO}
          selectedWorkerId={selectedWorkerId}
          selectedProjectId={selectedProjectId}
          projectsMap={projectsMap}
          onDataSaved={onDataSaved}
        />
      )}

      {/* Home Tab Table */}
      {activeTab === 'home' && (
        <div className="tw-min-h-[16rem] tw-flex-1 tw-overflow-auto glass-scrollbar">
          {loading ? (
            <EntryEmpty loading text={label('entry.loading')} />
          ) : !selectedWorkerId ? (
            <EntryEmpty icon={LuClipboardList} text={label('entry.pickWorker')} />
          ) : rows.length === 0 ? (
            <EntryEmpty icon={LuClipboardList} text={label('entry.noHomeVisits')} actionLabel={label('entry.addRow')} onAction={addRow} />
          ) : (
            <EntryGrid
              groups={groups}
              columns={columns}
              rows={filteredRows}
              showFilters={showFilters}
              toggleFilters={toggleFilters}
              filters={filters}
              setFilter={setFilter}
              onRemove={removeRow}
            />
          )}
        </div>
      )}
    </Modal>
  );
};

ManualDataEntryModal.propTypes = {
  show: PropTypes.bool,
  onHide: PropTypes.func.isRequired,
  workers: PropTypes.array.isRequired,
  selectedSMO: PropTypes.object,
  onDataSaved: PropTypes.func,
};

export default ManualDataEntryModal;
